import { Icon } from '@ui/themed';
import { t } from 'i18next';
import { type DragEvent, type ReactNode, useState } from 'react';
import { cn } from '../../../lib/styles';

type SortableListProps<T> = {
  items: T[];
  getKey: (item: T, index: number) => string;
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  dragActivation?: 'handle' | 'row';
};

function reorder<T>(items: T[], from: number, to: number): T[] {
  if (from === to || to < 0 || to >= items.length) return items;
  const next = [...items];
  const moved = next[from];
  if (moved === undefined) return items;
  next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function SortableList<T>({
  items,
  getKey,
  onReorder,
  renderItem,
  dragActivation = 'handle',
}: SortableListProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const isRowDraggable = dragActivation === 'row';

  function reset() {
    setDragIndex(null);
    setOverIndex(null);
  }

  function handleDrop(target: number) {
    if (dragIndex !== null) {
      onReorder(reorder(items, dragIndex, target));
    }
    reset();
  }

  function handleDragStart(index: number, event: DragEvent<HTMLElement>) {
    const dragImage = event.currentTarget.closest('li') ?? event.currentTarget;
    const rect = dragImage.getBoundingClientRect();

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', '');
    event.dataTransfer.setDragImage(dragImage, event.clientX - rect.left, event.clientY - rect.top);
    setDragIndex(index);
  }

  return (
    <ul className="flex flex-col gap-2">
      {items.map((item, index) => (
        <li
          key={getKey(item, index)}
          draggable={isRowDraggable}
          onDragStart={isRowDraggable ? (event) => handleDragStart(index, event) : undefined}
          onDragEnter={() => setOverIndex(index)}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => handleDrop(index)}
          onDragEnd={isRowDraggable ? reset : undefined}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-platform-primary/30 bg-platform-primary/5 p-3',
            isRowDraggable && 'cursor-grab active:cursor-grabbing',
            overIndex === index && dragIndex !== null && 'border-platform-primary',
            dragIndex === index && 'opacity-50',
          )}
        >
          {isRowDraggable ? (
            <span aria-hidden className="shrink-0 select-none">
              <Icon name="dragHandle" />
            </span>
          ) : (
            <button
              type="button"
              draggable
              aria-label={t('features:storefront_options.components.sortable_list.drag_handle')}
              onDragStart={(event) => handleDragStart(index, event)}
              onDragEnd={reset}
              className="shrink-0 cursor-grab select-none active:cursor-grabbing"
            >
              <Icon name="dragHandle" />
            </button>
          )}
          <div className="min-w-0 flex-1">{renderItem(item, index)}</div>
        </li>
      ))}
    </ul>
  );
}
